// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseTable } from '~/components/common';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { IHeaderCellType } from '~/utils/interface/common';
import { generateUniqueId } from '~/utils/helper/common';
import { TypeErrorFormEnum } from '~/utils/enum/common';
import { IChecklistForm, IAddChecklistItem } from '~/utils/interface/checklist';
// Styles, images, icons
import styles from './ChecklistItemForm.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const ChecklistItemForm = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/checklist', 'common/base']);

  const {
    control,
    watch,
    setError,
    formState: { errors },
  } = useFormContext<IChecklistForm>();

  const { append: appendItems, remove: removeItems } = useFieldArray({
    control,
    name: 'checklistItems',
  });

  const { checklistItems: listChecklists } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddItem = () => {
    const newItem: IAddChecklistItem = {
      id: generateUniqueId(),
      name: EMPTY_STRING,
      description: EMPTY_STRING,
    };

    appendItems(newItem);
    setError('checklistItems', { type: TypeErrorFormEnum.MANUAL, message: EMPTY_STRING });
  };

  const columnsItem = (t: TFunction): IHeaderCellType<IAddChecklistItem>[] => {
    return [
      {
        id: 'name',
        label: t('checklist_form_title_name'),
        render: (record: IAddChecklistItem, index: number) => {
          return (
            <Controller
              key={`${record.id}-${index}-name`}
              name={`checklistItems.${index}.name`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput height={32} borderRadius={'4px'} value={value} onChange={onChange} />
              )}
            />
          );
        },
        minWidth: 100,
      },
      {
        id: 'description',
        label: t('checklist_form_title_description'),
        render: (record: IAddChecklistItem, index: number) => {
          return (
            <Controller
              key={`${record.id}-${index}-description`}
              name={`checklistItems.${index}.description`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput height={32} borderRadius={'4px'} value={value} onChange={onChange} />
              )}
            />
          );
        },
        minWidth: 100,
      },
      {
        id: 'action',
        label: t('checklist_form_title_action'),
        minWidth: 100,
        align: 'center',
        render: (record: IAddChecklistItem, index: number) => {
          return (
            <Stack direction={'row'} gap={1} justifyContent={'center'}>
              <IconButton
                className={cx('buttonIcon')}
                onClick={() => {
                  removeItems(index);
                }}
              >
                <img
                  className={cx('iconDelete')}
                  src={icons.commonIconTrash}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                />
              </IconButton>
            </Stack>
          );
        },
      },
    ];
  };
  //#endregion Handle Function

  return (
    <Stack id='checklistItemFormComponent' className={cx('container')}>
      <Typography className={cx('header')}>{t('checklist_form_title_checklists')}</Typography>

      <BaseTable columns={columnsItem(t)} dataSource={listChecklists} />

      <Box>
        <Button
          type='button'
          variant='outlined'
          onClick={handleAddItem}
          startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
        >
          {t('common_text_add_item', { ns: 'common/base' })}
        </Button>
      </Box>
    </Stack>
  );
};

export default ChecklistItemForm;
